import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { PremiseConfig } from "@shared/types/foyer";
import { useContainerSize } from "../../hooks/useContainerSize";
import { usePreviewConfig } from "../../hooks/usePreviewConfig";
import { tvBase } from "../../utils/preview";
import { foyerApi } from "../../http/foyer";

const TV_W = 1920;
const TV_H = 1080;
/** Keep the preview a glanceable thumbnail, never full-bleed. */
const MAX_W = 460;

interface Props {
  premise: PremiseConfig;
  /** Open the preview ON this TV screen instead of home. The editor passes the
   *  screen the operator is currently filling in, so the preview shows the page
   *  being edited rather than the tile that opens it. */
  screen?: string;
  /** Cap on the rendered width. Defaults to a glanceable editor thumbnail. */
  maxWidth?: number;
  /** Drop the "Live preview" overline - for a page that heads it itself. */
  hideLabel?: boolean;
}

/**
 * Live preview of the TV scheme, embedded in the editor. Loads the real TV app
 * in an iframe (postMessage-driven `cms` mode) and pushes the current draft to
 * it on every change - edits appear instantly, no save or popup. The 1920×1080
 * frame is scaled down to a compact thumbnail (capped at MAX_W).
 */
export default function LivePreview({
  premise,
  screen,
  maxWidth = MAX_W,
  hideLabel = false,
}: Props) {
  const { t } = useTranslation("templates");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [wrapRef, size] = useContainerSize<HTMLDivElement>();
  const targetW = Math.min(size.width || maxWidth, maxWidth);
  const scale = targetW / TV_W;

  // The global radio + app catalogues merged with this premise's custom/disabled
  // lists, so the preview matches the live screen.
  const config = usePreviewConfig(premise) ?? premise;

  /**
   * The room's real catalogue, fetched HERE and posted in.
   *
   * 🚨 The preview cannot fetch it itself: `/tv/*` authenticates with a screen
   * token, the only credential a panel holds, and a preview has no pairing. The
   * CMS is already authenticated, so the parent reads it and hands it over the
   * channel the config already travels on.
   *
   * Undefined until it lands, and undefined on failure — which the TV reads as
   * "not asked for, or still on the wire", never as "empty". That contract is
   * what keeps the Film & TV tile off the screen until the answer is in rather
   * than showing an empty shelf.
   */
  const [vod, setVod] = useState<unknown>();
  useEffect(() => {
    let live = true;
    if (!premise.premiseId) return;
    foyerApi
      .tvPreview(premise.premiseId)
      .then((res) => live && setVod(res))
      .catch(() => {
        /* A preview without a catalogue is the launcher, which is still worth
           looking at. Nothing here is worth an error banner over. */
      });
    return () => {
      live = false;
    };
  }, [premise.premiseId]);

  useEffect(() => {
    const send = () => {
      const w = iframeRef.current?.contentWindow;
      w?.postMessage({ type: "foyer-preview-config", config }, "*");
      if (vod) w?.postMessage({ type: "foyer-preview-vod", vod }, "*");
    };
    send();
    function onReady(e: MessageEvent) {
      if (
        (e.data as { type?: string } | null)?.type === "foyer-preview-ready"
      ) {
        send();
      }
    }
    window.addEventListener("message", onReady);
    return () => window.removeEventListener("message", onReady);
  }, [config, vod]);

  return (
    <Box ref={wrapRef}>
      {!hideLabel && (
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          {t("livePreview.title")}
        </Typography>
      )}
      <Box
        sx={{
          width: targetW,
          height: TV_H * scale,
          overflow: "hidden",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "#000",
        }}
      >
        <iframe
          ref={iframeRef}
          title={t("livePreview.title")}
          src={`${tvBase()}/?preview=1&cms=1${
            screen ? `&screen=${encodeURIComponent(screen)}` : ""
          }`}
          style={{
            width: TV_W,
            height: TV_H,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
}
